/**
 * Card Grid Swatches Web Component
 *
 * Handles swatch interactions on the product *collection grid* (not the PDP).
 *
 * Behavior:
 * - Swatch click: swaps the parent card's hero <img> to show that sibling's
 *   featured image without triggering page navigation.
 * - Marks the clicked swatch as visually active (ring highlight).
 * - Writes sessionStorage.siblingSwatches_activeTab so that if the customer
 *   subsequently clicks the card title/image link, the PDP opens with the
 *   correct swatch tab already selected.
 * - Updates all card anchor links to point to the sibling product URL,
 *   preserving the ?collection= param using data-collection-handle.
 * - After optimistic image/link update, fetches the sibling's rendered card
 *   HTML to update title, price, and badges. Previous in-flight fetches are
 *   aborted via AbortController so rapid swatch clicks don't race.
 * - Truncates overflowing swatches via ResizeObserver; a "+N" pill shows the
 *   hidden count. No hardcoded breakpoints — the container width decides.
 *
 * Dependencies: none (vanilla ES2020)
 * Mounted by: snippets/card-sibling-swatches.liquid
 */

// -----------------------------------------------------------------------------
// Overflow truncation scheduler (shared by every card on the page).
//
// A collection page mounts up to ~64 of these. The old per-card ResizeObserver
// reset `hidden`, then read scrollWidth/offsetWidth, then wrote again, per card,
// per layout change: dozens of forced synchronous layouts while images load.
// Now: ONE ResizeObserver, one rAF flush, a read-only measuring pass (pure
// arithmetic from cached swatch/pill widths), then a write pass that only
// touches attributes whose state actually changes.
// -----------------------------------------------------------------------------
const CGS_PENDING = new Set();
let cgsRaf = 0;
let cgsPillWidth = 0; // worst-case "+99" pill width, cached; reset on window resize
let cgsResizeTimer = 0;

const cgsSharedRO = new ResizeObserver(entries => {
  for (const entry of entries) {
    const el = entry.target;
    const w = Math.round(entry.contentRect.width);
    if (w === el._lastWidth) continue; // height-only change (e.g. from hiding swatches): ignore
    el._lastWidth = w;
    CGS_PENDING.add(el);
  }
  if (CGS_PENDING.size && !cgsRaf) cgsRaf = requestAnimationFrame(cgsFlush);
});

window.addEventListener('resize', () => {
  clearTimeout(cgsResizeTimer);
  cgsResizeTimer = setTimeout(() => { cgsPillWidth = 0; }, 150);
}, { passive: true });

function cgsFlush() {
  cgsRaf = 0;
  const els = [...CGS_PENDING].filter(el => el.isConnected);
  CGS_PENDING.clear();
  if (!els.length) return;

  // Measure the pill once per flush batch (one write + one read), then reuse.
  if (!cgsPillWidth) {
    const probe = els.map(el => el.querySelector('.card-swatches__overflow-pill')).find(Boolean);
    if (probe) {
      const prevText = probe.textContent;
      const wasHidden = probe.hasAttribute('hidden');
      probe.style.visibility = 'hidden';
      probe.removeAttribute('hidden');
      probe.textContent = '+99';
      cgsPillWidth = probe.offsetWidth;
      probe.textContent = prevText;
      if (wasHidden) probe.setAttribute('hidden', '');
      probe.style.visibility = '';
    }
  }

  // READ pass: no DOM writes in here, so layout is computed at most once.
  const plans = els.map(el => el._planTruncation(cgsPillWidth));
  // WRITE pass.
  els.forEach((el, i) => { if (plans[i]) el._commitTruncation(plans[i]); });
}

if (!customElements.get('card-grid-swatches')) {
  class CardGridSwatches extends HTMLElement {
    constructor() {
      super();
      this._onSwatchClick = this._onSwatchClick.bind(this);
      this._onHostClick = this._onHostClick.bind(this);
      this._fetchController = null;
      this._lastWidth = -1;
    }

    connectedCallback() {
      this._cardId          = this.dataset.cardId;
      this._tabName         = this.dataset.collectionTitle || '';
      this._collectionHandle = this.dataset.collectionHandle || '';
      this._cardWrapper     = this._findCardWrapper();

      // One delegated listener instead of one per swatch (up to 13 per card).
      this.addEventListener('click', this._onHostClick);

      this._lastWidth = -1;
      cgsSharedRO.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onHostClick);
      if (this._fetchController) {
        this._fetchController.abort();
        this._fetchController = null;
      }
      cgsSharedRO.unobserve(this);
      CGS_PENDING.delete(this);
    }

    _onHostClick(event) {
      const swatch = event.target.closest && event.target.closest('.sibling-swatches__swatch');
      if (!swatch || !this.contains(swatch)) return;
      this._onSwatchClick(event, swatch);
    }

    // -------------------------------------------------------------------------
    // Overflow truncation. Hides swatches that don't fit in the container width
    // and shows a "+N" pill. Split into a read-only plan and a write commit so
    // the shared scheduler can batch every card without layout thrashing.
    // -------------------------------------------------------------------------

    _planTruncation(pillWidth) {
      const swatches = this._swatchList || (this._swatchList = [...this.querySelectorAll('.sibling-swatches__swatch')]);
      const pill = this._pill || (this._pill = this.querySelector('.card-swatches__overflow-pill'));
      if (!swatches.length || !pill) return null;
      const cw = this.clientWidth;
      if (!cw) return null;

      const total = parseInt(this.dataset.totalSiblings, 10) || swatches.length;
      const gap = parseFloat(getComputedStyle(this).gap) || 5;
      // swatch[0] is never hidden (maxVisible >= 1), so its width is always measurable
      const sw = swatches[0].offsetWidth;
      if (!sw) return null;

      const n = swatches.length;
      const allFit = n * sw + (n - 1) * gap <= cw;
      if (allFit && total <= n) return { visible: n, pillText: null };

      const available = cw - (pillWidth || 0) - gap;
      const maxVisible = Math.max(1, Math.floor((available + gap) / (sw + gap)));
      if (maxVisible >= total) return { visible: n, pillText: null };

      const visible = Math.min(maxVisible, n);
      return { visible, pillText: `+${total - visible}` };
    }

    _commitTruncation(plan) {
      const swatches = this._swatchList;
      const pill = this._pill;
      for (let i = 0; i < swatches.length; i++) {
        const shouldHide = i >= plan.visible;
        if (swatches[i].hasAttribute('hidden') !== shouldHide) swatches[i].toggleAttribute('hidden', shouldHide);
      }
      if (plan.pillText === null) {
        if (!pill.hasAttribute('hidden')) pill.setAttribute('hidden', '');
      } else {
        if (pill.textContent !== plan.pillText) pill.textContent = plan.pillText;
        if (pill.hasAttribute('hidden')) pill.removeAttribute('hidden');
      }
    }

    // -------------------------------------------------------------------------
    // Find the parent card wrapper that contains the hero <img>
    // -------------------------------------------------------------------------

    _findCardWrapper() {
      // The card-sibling-swatches element sits inside .card__content,
      // which is a sibling of .card__inner (which contains .card__media > .media > img).
      // Walk up to the card-wrapper.
      let el = this.parentElement;
      while (el && !el.classList.contains('card-wrapper')) {
        el = el.parentElement;
      }
      return el || null;
    }

    // -------------------------------------------------------------------------
    // Swatch click — swap image, update active state, persist tab, fetch card
    // -------------------------------------------------------------------------

    _onSwatchClick(event, clickedSwatch) {
      event.preventDefault();
      event.stopPropagation();

      const swatch     = clickedSwatch || event.currentTarget;
      const imgSrc     = swatch.dataset.imgSrc;
      const imgSrcset  = swatch.dataset.imgSrcset;
      const productUrl = swatch.dataset.productUrl;

      // 1. Swap the card hero image optimistically
      if (imgSrc && this._cardWrapper) {
        this._swapHeroImage(imgSrc, imgSrcset);
      }

      // 2. Update the card title/image links to point to the sibling product,
      //    preserving the ?collection= param via this._collectionHandle.
      if (productUrl && this._cardWrapper) {
        this._updateCardLinks(productUrl);
      }

      // 3. Persist the active tab name for PDP tab restoration
      if (this._tabName) {
        try {
          sessionStorage.setItem('siblingSwatches_activeTab', this._tabName);
        } catch (_) {}
      }

      // 4. Visually mark the clicked swatch as active
      this._setActiveSwatch(swatch);

      // 5. Fetch the sibling's rendered card to update title, price, badges.
      //    Abort any previous in-flight request to prevent race conditions.
      if (productUrl && this._cardWrapper) {
        if (this._fetchController) {
          this._fetchController.abort();
        }
        this._fetchController = new AbortController();

        const collectionParam = this._collectionHandle
          ? `collection=${encodeURIComponent(this._collectionHandle)}&`
          : '';
        const fetchUrl = `${productUrl}?${collectionParam}section_id=card-product-section`;

        fetch(fetchUrl, { signal: this._fetchController.signal })
          .then(res => res.text())
          .then(responseText => {
            this._fetchController = null;

            const parser = new DOMParser();
            const doc = parser.parseFromString(responseText, 'text/html');
            const newCardEl = doc.querySelector('.card-wrapper.product-card-wrapper');

            if (!newCardEl) return;

            // Capture old wrapper now, before any DOM moves trigger connectedCallback
            const oldWrapper = this._cardWrapper;

            // a. Preserve the optimistically swapped images to avoid re-flash
            const currentMedia = oldWrapper.querySelector('.card__media .media');
            const newMedia = newCardEl.querySelector('.card__media .media');
            if (currentMedia && newMedia) {
              newMedia.innerHTML = currentMedia.innerHTML;
            }

            // b. Preserve the sibling swatches element (fetched section can't
            //    render swatches — it lacks the section schema settings).
            const currentSwatches = oldWrapper.querySelector('card-grid-swatches');
            const newSwatchesPlaceholder = newCardEl.querySelector('card-grid-swatches');

            if (currentSwatches) {
              if (newSwatchesPlaceholder) {
                newSwatchesPlaceholder.replaceWith(currentSwatches);
              } else {
                // Insert before .card__information if no placeholder exists
                const cardContent = newCardEl.querySelector('.card__content');
                const cardInfo = newCardEl.querySelector('.card__information');
                if (cardContent && cardInfo) {
                  cardContent.insertBefore(currentSwatches, cardInfo);
                }
              }
            }

            // c. Swap the old card wrapper out of the DOM
            try {
              if (oldWrapper && oldWrapper.parentNode) {
                oldWrapper.replaceWith(newCardEl);
              }
            } catch (e) {
              console.error('[card-grid-swatches] DOM replacement error:', e);
            }

            // d. Update stored reference and fix the fetched card's links
            //    (card-product-section renders without collection context,
            //    so links lack the ?collection= param — apply it now).
            this._cardWrapper = newCardEl;
            if (productUrl) {
              this._updateCardLinks(productUrl);
            }
          })
          .catch(err => {
            if (err.name === 'AbortError') return; // expected on rapid clicks
            console.error('[card-grid-swatches] Fetch error:', err);
          });
      }
    }

    // -------------------------------------------------------------------------
    // Swap the hero image(s) inside .card__media on the parent card
    //
    // IMPORTANT: We intentionally DO NOT remove .media--hover-effect.
    // base.css contains: ".media.media--hover-effect > img + img { opacity: 0 }"
    // which is what hides the secondary (hover) image by default. Removing the
    // class would cause img+img to revert to browser-default opacity:1, making
    // it always visible on top of img0 and blocking the swapped hero image.
    //
    // Instead we update BOTH img0 (primary) and img1 (hover/secondary) to the
    // new swatch src so both hover and non-hover states show the correct colour.
    // -------------------------------------------------------------------------

    _swapHeroImage(src, srcset) {
      const media = this._cardWrapper.querySelector('.card__media .media');
      if (!media) return;

      const imgs = Array.from(media.querySelectorAll('img'));
      if (!imgs.length) return;

      const effectiveSrcset = srcset || (src && src.includes('width=533')
        ? [165, 360, 533, 720, 940].map(w => `${src.replace('width=533', 'width=' + w)} ${w}w`).join(', ')
        : '');

      imgs.forEach(img => {
        // Clone and replace to force an immediate repaint on all browsers
        // (Safari can cache srcset and not repaint the updated image otherwise)
        const newImg = img.cloneNode();
        newImg.src = src;
        if (effectiveSrcset) {
          newImg.srcset = effectiveSrcset;
        } else {
          newImg.removeAttribute('srcset');
        }
        img.parentNode.replaceChild(newImg, img);
      });
    }

    // -------------------------------------------------------------------------
    // Update all anchor links on the card (title, image link) to the sibling URL.
    // Collection param is read from this._collectionHandle (set at connect time
    // from data-collection-handle) — not from existing link hrefs, which may be
    // stale after a previous fetch-and-swap cycle.
    // -------------------------------------------------------------------------

    _updateCardLinks(productUrl) {
      const collectionParam = this._collectionHandle
        ? '?collection=' + encodeURIComponent(this._collectionHandle)
        : '';
      const newHref = productUrl + collectionParam;

      this._cardWrapper.querySelectorAll('a.full-unstyled-link').forEach(a => {
        a.setAttribute('href', newHref);
      });
    }

    // -------------------------------------------------------------------------
    // Toggle active swatch ring
    // -------------------------------------------------------------------------

    _setActiveSwatch(activeSwatch) {
      this.querySelectorAll('.sibling-swatches__swatch').forEach(sw => {
        sw.classList.remove('sibling-swatches__swatch--active');
        sw.removeAttribute('aria-current');
      });

      activeSwatch.classList.add('sibling-swatches__swatch--active');
      activeSwatch.setAttribute('aria-current', 'true');
    }
  }

  customElements.define('card-grid-swatches', CardGridSwatches);
}
